import { useState, useMemo, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/useAuth"
import { Search, Plus, Pencil, Trash2, Eye, FileText, Handshake, Sparkles, Loader2, Key, ChevronRight, ChevronDown, Copy, ExternalLink, MoreHorizontal, CheckCircle2, CalendarX, PenLine } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { DataTable, DetailList, DetailSection, DetailSheet, EmptyState, Page, PageHeader, Pagination, Panel, SearchInput, Segmented, StatCard, StatGrid, Tag, Toolbar, fmtDateBR, rowClass, td, th, usePagination, type Tone } from "@/components/superadmin/kit"
import { cn } from "@/lib/utils"
import type { Contrato, StatusContrato, ModeloContrato } from "@/types/comercial"
import { encodePayload } from "@/utils/shareEncoder"
import { supabase } from "@/integrations/supabase/client"
import { gerarTextoIA, modeloIAPadrao, useModelosIA } from "@/lib/ai"

// ── Helpers ──────────────────────────────────────────────────────────────────
function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
function fmtCurrency(v: number) { return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v) }
function fmtDate(d: string) { return d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—" }

const STATUS_LABELS: Record<StatusContrato, string> = {
  rascunho: "Rascunho",
  pendente_assinatura: "Aguardando assinatura",
  ativo: "Ativo",
  vencido: "Vencido",
  cancelado: "Cancelado",
}
const STATUS_TONE: Record<StatusContrato, Tone> = {
  rascunho: "neutral",
  pendente_assinatura: "warning",
  ativo: "success",
  vencido: "danger",
  cancelado: "neutral",
}

const MODELOS_PADRAO: ModeloContrato[] = [
  {
    id: "m1",
    nome: "Contrato de Gestão de Condomínio",
    descricao: "Modelo padrão para prestação de serviços de gestão condominial.",
    conteudo: `CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE GESTÃO CONDOMINIAL

CONTRATANTE: {{NOME_CLIENTE}}
EMPRESA/CONDOMÍNIO: {{EMPRESA_CLIENTE}}
E-MAIL: {{EMAIL_CLIENTE}}

CONTRATADA: ItaCondo Sistemas

OBJETO:
Prestação de serviços de gestão condominial, incluindo os itens descritos na Proposta Comercial aceita em {{DATA_ACEITE}}.

VALOR:
O valor total dos serviços é de {{VALOR_TOTAL}}, a ser pago conforme acordado.

VIGÊNCIACIA:
O contrato tem início em {{DATA_INICIO}} e vigência até {{DATA_FIM}}.

DISPOSIÇÕES GERAIS:
As partes se comprometem a cumprir as obrigaçõeses aqui estabelecidas, podendo rescindir o contrato com aviso prévio de 30 dias.

ItaCondo Sistemas
CNPJ: XX.XXX.XXX/XXXX-XX`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
]

interface Signatario {
  nome: string
  email: string
  role: string
  status: "assinado" | "pendente"
  dataAssinatura?: string
  ipAddress?: string | null
  assinaturaBase64?: string
}

interface AuditEvent {
  timestamp: string
  action: string
  details: string
}

interface ContratoExtendido extends Contrato {
  envelopeId?: string
  signatarios?: Signatario[]
  auditLog?: AuditEvent[]
}

const EMPTY_CONTRATO = (): ContratoExtendido => ({
  id: uid(),
  titulo: "",
  clienteNome: "",
  clienteEmail: "",
  clienteEmpresa: "",
  status: "rascunho",
  conteudo: "",
  valor: 0,
  dataInicio: new Date().toISOString().split("T")[0],
  observacoes: "",
  senha: "",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
})

// Envelope padrão para contratos que ainda não têm um: todas as partes
// pendentes. Assinaturas, IPs e eventos só entram quando acontecem de fato
// (RPC assinar_contrato ou marcação manual registrada abaixo).
function ensureSignatureFields(c: ContratoExtendido): ContratoExtendido {
  const envelopeId = `#${c.id.slice(0, 8).toUpperCase()}`
  if (c.signatarios && c.signatarios.length > 0) {
    return { ...c, envelopeId: c.envelopeId ?? envelopeId }
  }

  const signatarios: Signatario[] = [
    {
      nome: c.clienteNome,
      email: c.clienteEmail || "",
      role: "Contratante (Síndico/Representante)",
      status: "pendente",
    },
    {
      nome: "ItaCondo Sistemas & Integrações",
      email: "diretoria@itacondo.com.br",
      role: "Contratada (Representante Legal)",
      status: "pendente",
    }
  ]

  const auditLog: AuditEvent[] = c.auditLog?.length ? c.auditLog : [
    {
      timestamp: c.createdAt,
      action: "Envelope Criado",
      details: "Contrato criado no itaCondo."
    }
  ]

  return {
    ...c,
    envelopeId,
    signatarios,
    auditLog
  }
}



const Contratos = () => {
  const { toast } = useToast()
  const { profile } = useAuth()
  const [searchParams] = useSearchParams()

  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Carregar do banco de dados (Supabase) ou mock padrão
  const [contratos, setContratos] = useState<ContratoExtendido[]>([])
  const [loading, setLoading] = useState(true)
  const [propostas, setPropostas] = useState<any[]>([])
  const [loadingPropostas, setLoadingPropostas] = useState(false)

  useEffect(() => {
    const fetchContratos = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from("contratos")
          .select("*")
          .order("created_at", { ascending: false })

        if (error) throw error

        let dbContratos = data || []

        if (dbContratos.length > 0) {
          const mapped: ContratoExtendido[] = dbContratos.map((item: any) => {
            let signatarios = undefined
            let auditLog = undefined
            let actualObservacoes = item.observacoes || ""

            if (item.observacoes && item.observacoes.startsWith("{")) {
              try {
                const parsed = JSON.parse(item.observacoes)
                signatarios = parsed.signatarios
                auditLog = parsed.auditLog
                actualObservacoes = parsed.internas
              } catch {}
            }

            const base = {
              id: item.id,
              titulo: item.titulo,
              clienteNome: item.cliente_nome,
              clienteEmail: item.cliente_email || "",
              clienteEmpresa: item.cliente_empresa || undefined,
              status: item.status as StatusContrato,
              conteudo: item.conteudo || "",
              modeloId: item.modelo_id || undefined,
              propostaId: item.proposta_id || undefined,
              valor: item.valor || 0,
              dataInicio: item.data_inicio,
              dataFim: item.data_fim || undefined,
              dataAssinatura: item.data_assinatura || undefined,
              observacoes: actualObservacoes,
              senha: item.senha || undefined,
              createdAt: item.created_at,
              updatedAt: item.updated_at,
              signatarios,
              auditLog
            }

            return base.signatarios ? base : ensureSignatureFields(base)
          })
          setContratos(mapped)
          localStorage.setItem("itacondo_contratos", JSON.stringify(mapped))
        } else {
          const raw = localStorage.getItem("itacondo_contratos")
          setContratos(raw ? JSON.parse(raw) : [])
        }
      } catch (err) {
        console.error("Erro ao buscar contratos no banco:", err)
        const raw = localStorage.getItem("itacondo_contratos")
        if (raw) setContratos(JSON.parse(raw))
      } finally {
        setLoading(false)
      }
    }

    const fetchPropostas = async () => {
      try {
        setLoadingPropostas(true)
        const { data, error } = await supabase
          .from("propostas")
          .select("id, titulo, cliente_nome, cliente_empresa, cliente_email, valor_total, valor_final, status, aceite_em")
          .order("created_at", { ascending: false })

        if (error) throw error
        if (data) {
          setPropostas(data)
        }
      } catch (err) {
        console.error("Erro ao buscar propostas no banco:", err)
      } finally {
        setLoadingPropostas(false)
      }
    }

    fetchContratos()
    fetchPropostas()
  }, [])

  // Efeito para salvar no localStorage de backup
  useEffect(() => {
    if (contratos.length > 0) {
      localStorage.setItem("itacondo_contratos", JSON.stringify(contratos))
    }
  }, [contratos])
  
  // IA (provedor configurado na Edge Function ai-generate)
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState("")
  const [aiModel, setAiModel] = useState(modeloIAPadrao)
  const { modelos: aiModels } = useModelosIA(aiModel)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedContent, setGeneratedContent] = useState("")

  const handleGenerateContract = async () => {
    if (!aiPrompt.trim()) {
      toast({ title: "Erro", description: "Digite o escopo ou descrição do contrato", variant: "destructive" })
      return
    }

    setIsGenerating(true)
    try {
      const content = await gerarTextoIA(
        [
            {
              role: "system",
              content: `Você é um advogado sênior e desenvolvedor de negócios especializado em contratos comerciais de tecnologia, facilities e serviços condominiais no Brasil. 
Crie um contrato estruturado, formal e robusto em português brasileiro, seguindo as leis brasileiras (como Código Civil).
Use os seguintes placeholders no texto do contrato para que possam ser substituídos automaticamente:
{{NOME_CLIENTE}} - para o nome do cliente contratante
{{EMPRESA_CLIENTE}} - para a empresa/condomínio contratante
{{EMAIL_CLIENTE}} - para o e-mail do cliente
{{VALOR_TOTAL}} - para o valor total do contrato
{{DATA_INICIO}} - para a data de início do contrato
{{DATA_FIM}} - para a data de término (se aplicável)

Retorne APENAS o texto do contrato propriamente dito, sem saudaçõeses, introduçõeses ou explicaçõeses no início ou no fim.`
            },
            {
              role: "user",
              content: `Por favor, gere um contrato com as seguintes especificaçõeses:\nTítulo: ${form.titulo || "Contrato de Prestação de Serviços"}\nCliente: ${form.clienteNome || "Contratante"}\nEmpresa: ${form.clienteEmpresa || ""}\nValor: R$ ${form.valor || "0,00"}\nDescrição/Escopo: ${aiPrompt}`
            }
        ],
        { model: aiModel, temperature: 0.3 },
      )
      setGeneratedContent(content)
      toast({ title: "Contrato gerado!", description: "Revise o conteúdo no painel ao lado." })
    } catch (error: any) {
      console.error(error)
      toast({ title: "Erro ao gerar", description: error.message || "Erro desconhecido ao chamar a IA", variant: "destructive" })
    } finally {
      setIsGenerating(false)
    }
  }

  const applyGeneratedContent = () => {
    setForm(prev => ({ ...prev, conteudo: generatedContent }))
    setIsAiDialogOpen(false)
    setGeneratedContent("")
    setAiPrompt("")
    toast({ title: "Conteúdo aplicado", description: "O contrato gerado pela IA foi inserido no editor." })
  }
  
  const [modelos] = useState<ModeloContrato[]>(MODELOS_PADRAO)
  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(searchParams.get("action") === "new")
  const [editing, setEditing] = useState<ContratoExtendido | null>(null)
  const [viewing, setViewing] = useState<ContratoExtendido | null>(null)
  const [form, setForm] = useState<ContratoExtendido>(EMPTY_CONTRATO())
  const [modelosOpen, setModelosOpen] = useState(false)

  // Prefill contract from proposal details if prefill_proposta_id exists in URL
  useEffect(() => {
    const propId = searchParams.get("prefill_proposta_id")
    if (propId) {
      const loadPrefill = async () => {
        try {
          const { data: p, error } = await supabase
            .from("propostas")
            .select("*")
            .eq("id", propId)
            .maybeSingle()

          if (error) throw error

          if (p) {
            const clientName = p.cliente_nome
            const clientEmpresa = p.cliente_empresa || ""
            const clientEmail = p.cliente_email || ""
            const valorFinal = p.valor_final || p.valor_total || 0
            
            // Apply default model text with placeholders prefilled
            const template = MODELOS_PADRAO[0].conteudo
            const filledContent = template
              .replace(/{{NOME_CLIENTE}}/g, clientName)
              .replace(/{{EMPRESA_CLIENTE}}/g, clientEmpresa || clientName)
              .replace(/{{EMAIL_CLIENTE}}/g, clientEmail)
              .replace(/{{VALOR_TOTAL}}/g, fmtCurrency(valorFinal))
              .replace(/{{DATA_ACEITE}}/g, p.aceite_em ? new Date(p.aceite_em).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR"))
              .replace(/{{DATA_INICIO}}/g, new Date().toLocaleDateString("pt-BR"))
              .replace(/{{DATA_FIM}}/g, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString("pt-BR"))

            setForm({
              id: uid(),
              titulo: `Contrato de Prestação de Serviços — ${clientEmpresa || clientName}`,
              clienteNome: clientName,
              clienteEmail: clientEmail,
              clienteEmpresa: clientEmpresa || "",
              status: "rascunho",
              conteudo: filledContent,
              valor: valorFinal,
              propostaId: p.id,
              modeloId: MODELOS_PADRAO[0].id,
              dataInicio: new Date().toISOString().split("T")[0],
              observacoes: `Gerado automaticamente a partir da proposta: ${p.titulo}`,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            })
            setEditorOpen(true)
            toast({
              title: "Dados da Proposta Carregados!",
              description: `O formulário foi preenchido com as informações de ${clientName}.`
            })
          }
        } catch (err) {
          console.error("Erro ao preencher contrato a partir de proposta:", err)
        }
      }
      loadPrefill()
    }
  }, [searchParams])

  const filtered = useMemo(() => contratos.filter(c => {
    const q = search.toLowerCase()
    return (c.titulo.toLowerCase().includes(q) || c.clienteNome.toLowerCase().includes(q)) &&
      (filterStatus === "all" || c.status === filterStatus)
  }), [contratos, search, filterStatus])

  const pager = usePagination(filtered, 12)
  useEffect(() => pager.reset(), [search, filterStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    total: contratos.length,
    ativos: contratos.filter(c => c.status === "ativo").length,
    pendentes: contratos.filter(c => c.status === "pendente_assinatura").length,
    vencidos: contratos.filter(c => c.status === "vencido").length,
  }), [contratos])

  const openNew = () => { setEditing(null); setForm(EMPTY_CONTRATO()); setEditorOpen(true) }
  const openEdit = (c: ContratoExtendido) => { setEditing(c); setForm({ ...c }); setEditorOpen(true) }
  const setField = (k: keyof ContratoExtendido, v: any) => setForm(p => ({ ...p, [k]: v }))

  const useModelo = (m: ModeloContrato) => {
    setForm(p => ({ ...p, conteudo: m.conteudo, modeloId: m.id }))
    setModelosOpen(false)
    toast({ title: "Modelo aplicado", description: m.nome })
  }

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.clienteNome.trim()) {
      toast({ title: "Erro", description: "Título e nome do cliente são obrigatórios", variant: "destructive" })
      return
    }

    const base = { ...form, updatedAt: new Date().toISOString() }
    const updated = ensureSignatureFields(base)

    const serializedObservacoes = JSON.stringify({
      internas: updated.observacoes || "",
      signatarios: updated.signatarios,
      auditLog: updated.auditLog
    })

    const isValidUuid = (val?: string) => {
      if (!val) return false
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      return uuidRegex.test(val)
    }

    const dbId = isValidUuid(updated.id) ? updated.id : uid()
    const dbModeloId = isValidUuid(updated.modeloId) ? updated.modeloId : null
    const dbPropostaId = isValidUuid(updated.propostaId) ? updated.propostaId : null

    try {
      const { error } = await supabase
        .from("contratos")
        .upsert({
          id: dbId,
          titulo: updated.titulo,
          cliente_nome: updated.clienteNome,
          cliente_email: updated.clienteEmail,
          cliente_empresa: updated.clienteEmpresa || null,
          status: updated.status,
          conteudo: updated.conteudo || null,
          modelo_id: dbModeloId,
          proposta_id: dbPropostaId,
          valor: updated.valor,
          data_inicio: updated.dataInicio,
          data_fim: updated.dataFim || null,
          data_assinatura: updated.dataAssinatura || null,
          observacoes: serializedObservacoes,
          senha: updated.senha || null,
          updated_at: updated.updatedAt
        })

      if (error) throw error

      setContratos(prev => {
        const idx = prev.findIndex(c => c.id === dbId)
        const finalObj = { ...updated, id: dbId }
        if (idx >= 0) { const arr = [...prev]; arr[idx] = finalObj; return arr }
        return [...prev, finalObj]
      })
      setEditorOpen(false)
      toast({ title: editing ? "Contrato atualizado" : "Contrato criado", description: form.titulo })
    } catch (err: any) {
      console.error("Erro ao salvar contrato no banco:", err)
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" })
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("contratos").delete().eq("id", id)
      if (error) throw error

      setContratos(prev => prev.filter(c => c.id !== id))
      toast({ title: "Contrato excluído" })
    } catch (err: any) {
      console.error("Erro ao excluir contrato:", err)
      toast({ title: "Erro ao excluir", description: err.message, variant: "destructive" })
    }
  }

  const handleStatus = async (id: string, status: StatusContrato) => {
    try {
      const target = contratos.find(c => c.id === id)
      if (!target) return

      const base = { ...target, status, updatedAt: new Date().toISOString() }
      const updated = ensureSignatureFields(base)

      const serializedObservacoes = JSON.stringify({
        internas: updated.observacoes || "",
        signatarios: updated.signatarios,
        auditLog: updated.auditLog
      })

      const { error } = await supabase
        .from("contratos")
        .update({
          status,
          observacoes: serializedObservacoes,
          data_assinatura: updated.dataAssinatura || null,
          updated_at: updated.updatedAt
        })
        .eq("id", id)

      if (error) throw error

      setContratos(prev => prev.map(c => {
        if (c.id === id) return updated
        return c
      }))
      toast({ title: `Status: ${STATUS_LABELS[status]}` })
    } catch (err: any) {
      console.error("Erro ao alterar status:", err)
      toast({ title: "Erro ao alterar status", description: err.message, variant: "destructive" })
    }
  }

  const handleCopyLink = (c: ContratoExtendido) => {
    try {
      const url = `${window.location.origin}/c/${c.id}`
      navigator.clipboard.writeText(url)
      toast({
        title: "Link Copiado!",
        description: "O link de assinatura eletrônica do contrato foi copiado para sua área de transferência."
      })
    } catch (e) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o link de assinatura.",
        variant: "destructive"
      })
    }
  }

  // Assinatura feita fora da plataforma (papel, outro serviço): registra só o
  // que aconteceu, quem marcou e quando. Não inventa IP nem assinatura digital.
  const handleMarcarAssinado = async (c: ContratoExtendido) => {
    const now = new Date().toISOString()
    const autor = profile?.full_name || profile?.email || "superadmin"
    const updated: ContratoExtendido = {
      ...c,
      status: "ativo" as const,
      auditLog: [
        ...(c.auditLog || []),
        { timestamp: now, action: "Marcado como Assinado", details: `Contrato marcado manualmente como assinado por ${autor} (assinatura realizada fora da plataforma).` },
      ],
      dataAssinatura: now,
      updatedAt: now,
    }
    const serialized = JSON.stringify({ internas: updated.observacoes || "", signatarios: updated.signatarios, auditLog: updated.auditLog })
    try {
      const { error } = await supabase
        .from("contratos")
        .update({ status: "ativo", observacoes: serialized, data_assinatura: updated.dataAssinatura, updated_at: updated.updatedAt })
        .eq("id", c.id)
      if (error) throw error
      setContratos(prev => prev.map(item => item.id === c.id ? updated : item))
      toast({ title: "Contrato marcado como assinado", description: "O contrato agora está ativo. A marcação manual ficou registrada na auditoria." })
    } catch (err: any) {
      console.error("Erro ao marcar contrato como assinado:", err)
      toast({ title: "Erro ao assinar", description: err.message, variant: "destructive" })
    }
  }

  const detail = contratos.find(c => c.id === expandedId) ?? null
  const valorAtivo = contratos.filter(c => c.status === "ativo").reduce((t, c) => t + Number(c.valor || 0), 0)

  return (
    <SuperAdminLayout>
      <Page>
        <PageHeader
          title="Contratos"
          description="Contratos gerados a partir das propostas, com assinatura eletrônica e trilha de auditoria."
          actions={
            <>
              {profile?.role === "superadmin" && (
                <Button variant="outline" className="h-9 gap-1.5" onClick={() => setIsAiDialogOpen(true)}>
                  <Sparkles className="h-4 w-4" />Gerar com IA
                </Button>
              )}
              <Button variant="outline" className="h-9 gap-1.5" onClick={() => setModelosOpen(true)}><FileText className="h-4 w-4" />Modelos</Button>
              <Button className="h-9 gap-1.5" onClick={openNew}><Plus className="h-4 w-4" />Novo contrato</Button>
            </>
          }
        />

        <StatGrid>
          <StatCard label="Ativos" value={stats.ativos} hint={`${fmtCurrency(valorAtivo)} em contratos`} icon={CheckCircle2} tone="success" />
          <StatCard label="Aguardando assinatura" value={stats.pendentes} hint={stats.pendentes ? "Cobre o cliente" : "Nada pendente"} icon={PenLine} tone={stats.pendentes ? "warning" : "neutral"} />
          <StatCard label="Vencidos" value={stats.vencidos} hint={stats.vencidos ? "Renove ou encerre" : "Nenhum vencido"} icon={CalendarX} tone={stats.vencidos ? "danger" : "neutral"} />
          <StatCard label="Total" value={stats.total} hint="Todos os status" icon={Handshake} />
        </StatGrid>

        <Panel flush>
          <Toolbar>
            <Segmented
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: "all", label: "Todos", count: contratos.length },
                ...(Object.keys(STATUS_LABELS) as StatusContrato[]).map(s => ({ value: s, label: STATUS_LABELS[s], count: contratos.filter(c => c.status === s).length })),
              ]}
            />
            <SearchInput value={search} onChange={setSearch} placeholder="Buscar por título ou cliente" className="lg:ml-auto" />
          </Toolbar>

          {filtered.length === 0 ? (
            <EmptyState
              icon={Handshake}
              title={contratos.length ? "Nenhum contrato neste filtro" : "Nenhum contrato ainda"}
              description={contratos.length ? "Ajuste a busca ou o status." : "Gere a partir de uma proposta aceita ou crie do zero."}
              action={!contratos.length && <Button size="sm" className="gap-1.5" onClick={openNew}><Plus className="h-4 w-4" />Novo contrato</Button>}
            />
          ) : (
            <>
              <DataTable minWidth={860}>
                <thead>
                  <tr className="border-b border-border">
                    <th className={th}>Contrato</th>
                    <th className={th}>Cliente</th>
                    <th className={cn(th, "text-right")}>Valor</th>
                    <th className={th}>Vigência</th>
                    <th className={th}>Status</th>
                    <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody>
                  {pager.slice.map(c => (
                    <tr key={c.id} className={rowClass} onClick={() => setExpandedId(c.id)}>
                      <td className={td}>
                        <div className="max-w-[300px] truncate text-[13px] font-medium text-foreground">{c.titulo}</div>
                        <div className="text-[12px] text-muted-foreground">criado {fmtDateBR(c.createdAt)}</div>
                      </td>
                      <td className={td}>
                        <div className="max-w-[220px] truncate text-[13px] text-foreground">{c.clienteNome}</div>
                        {c.clienteEmpresa && <div className="max-w-[220px] truncate text-[12px] text-muted-foreground">{c.clienteEmpresa}</div>}
                      </td>
                      <td className={cn(td, "text-right text-[13px] font-medium tabular-nums text-foreground")}>{fmtCurrency(c.valor)}</td>
                      <td className={cn(td, "text-[13px] tabular-nums text-muted-foreground")}>{fmtDate(c.dataInicio)}{c.dataFim ? ` → ${fmtDate(c.dataFim)}` : ""}</td>
                      <td className={td}><Tag tone={STATUS_TONE[c.status]} dot>{STATUS_LABELS[c.status]}</Tag></td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onSelect={() => setViewing(c)}><Eye className="mr-2 h-4 w-4" />Ler contrato</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => openEdit(c)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => handleCopyLink(c)}><Copy className="mr-2 h-4 w-4" />Copiar link de assinatura</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => window.open(`${window.location.origin}/c/${c.id}`, "_blank")}><ExternalLink className="mr-2 h-4 w-4" />Abrir página do cliente</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {c.status === "rascunho" && <DropdownMenuItem onSelect={() => handleStatus(c.id, "pendente_assinatura")}><PenLine className="mr-2 h-4 w-4" />Enviar para assinatura</DropdownMenuItem>}
                            {c.status === "pendente_assinatura" && <DropdownMenuItem onSelect={() => handleMarcarAssinado(c)}><Handshake className="mr-2 h-4 w-4" />Marcar como assinado</DropdownMenuItem>}
                            <DropdownMenuItem onSelect={() => setDeletingId(c.id)} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <Pagination {...pager} noun="contratos" />
            </>
          )}
        </Panel>
      </Page>

      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setExpandedId(null)}
        badge={detail && <Tag tone={STATUS_TONE[detail.status]} dot className="self-start">{STATUS_LABELS[detail.status]}</Tag>}
        title={detail?.titulo}
        subtitle={detail && `${detail.clienteNome}${detail.clienteEmpresa ? ` · ${detail.clienteEmpresa}` : ""}`}
        actions={detail && (
          <>
            <Button size="sm" className="h-8 gap-1.5" onClick={() => { openEdit(detail); setExpandedId(null) }}><Pencil className="h-3.5 w-3.5" />Editar</Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setViewing(detail)}><Eye className="h-3.5 w-3.5" />Ler contrato</Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => handleCopyLink(detail)}><Copy className="h-3.5 w-3.5" />Link</Button>
          </>
        )}
      >
        {detail && (
          <>
            <DetailSection title="Contrato">
              <DetailList items={[
                ["Valor", fmtCurrency(detail.valor)],
                ["Início", fmtDate(detail.dataInicio)],
                ["Fim", detail.dataFim ? fmtDate(detail.dataFim) : "Indeterminado"],
                ["Assinado em", detail.dataAssinatura ? fmtDateBR(detail.dataAssinatura, true) : null],
                ["E-mail do cliente", detail.clienteEmail],
                ["Envelope", detail.envelopeId ? <code key="env" className="font-mono text-[11px]">{detail.envelopeId}</code> : null],
                ["Proposta de origem", detail.propostaId ? <Link key="p" to="/gestao-comercial/propostas" className="text-[hsl(224_100%_76%)] hover:underline">Ver propostas</Link> : null],
              ]} />
            </DetailSection>

            <DetailSection title={`Signatários (${detail.signatarios?.length ?? 0})`}>
              {!detail.signatarios?.length ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Nenhum signatário definido.</p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {detail.signatarios.map((sig, idx) => (
                    <li key={idx} className="flex items-start gap-3 px-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] text-foreground">{sig.nome} <span className="text-muted-foreground">· {sig.role}</span></div>
                        <div className="truncate text-[11px] text-muted-foreground">{sig.email}</div>
                        {sig.dataAssinatura && <div className="text-[11px] tabular-nums text-muted-foreground">{fmtDateBR(sig.dataAssinatura, true)}{sig.ipAddress && ` · IP ${sig.ipAddress}`}</div>}
                        {sig.status === "assinado" && sig.assinaturaBase64 && (
                          <img src={sig.assinaturaBase64} alt="Assinatura" className="mt-1.5 h-8 rounded border border-dashed border-border bg-white/90 object-contain p-0.5" />
                        )}
                      </div>
                      <Tag tone={sig.status === "assinado" ? "success" : "warning"} dot>{sig.status === "assinado" ? "Assinado" : "Pendente"}</Tag>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>

            <DetailSection title="Trilha de auditoria">
              {!detail.auditLog?.length ? (
                <p className="rounded-lg border border-border p-3 text-[13px] text-muted-foreground">Sem eventos registrados.</p>
              ) : (
                <ol className="relative space-y-3 border-l border-border pl-5">
                  {[...detail.auditLog].reverse().map((log, idx) => (
                    <li key={idx} className="relative">
                      <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-[hsl(228_11%_6.5%)] bg-[hsl(225_5%_45%)]" />
                      <div className="text-[13px] text-foreground">{log.action}</div>
                      <div className="text-[12px] text-muted-foreground">{log.details}</div>
                      <div className="text-[11px] tabular-nums text-muted-foreground">{fmtDateBR(log.timestamp, true)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </DetailSection>
          </>
        )}
      </DetailSheet>

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
            <AlertDialogDescription>O contrato e o link de assinatura deixam de existir. Não dá para desfazer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deletingId) handleDelete(deletingId); setExpandedId(null) }} className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Contrato" : "Novo Contrato"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div>
                <Label htmlFor="prefillProposta">Preencher a partir de uma Proposta / Orçamento</Label>
                <Select
                  onValueChange={(val) => {
                    if (val === "none") return
                    const p = propostas.find(x => x.id === val)
                    if (p) {
                      const clientName = p.cliente_nome || ""
                      const clientEmpresa = p.cliente_empresa || ""
                      const clientEmail = p.cliente_email || ""
                      const valorFinal = p.valor_final || p.valor_total || 0
                      
                      // Apply default model text with placeholders prefilled
                      const template = MODELOS_PADRAO[0].conteudo
                      const filledContent = template
                        .replace(/{{NOME_CLIENTE}}/g, clientName)
                        .replace(/{{EMPRESA_CLIENTE}}/g, clientEmpresa || clientName)
                        .replace(/{{EMAIL_CLIENTE}}/g, clientEmail)
                        .replace(/{{VALOR_TOTAL}}/g, fmtCurrency(valorFinal))
                        .replace(/{{DATA_ACEITE}}/g, p.aceite_em ? new Date(p.aceite_em).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR"))
                        .replace(/{{DATA_INICIO}}/g, new Date().toLocaleDateString("pt-BR"))
                        .replace(/{{DATA_FIM}}/g, new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString("pt-BR"))

                      setForm(prev => ({
                        ...prev,
                        titulo: `Contrato de Prestação de Serviços — ${clientEmpresa || clientName}`,
                        clienteNome: clientName,
                        clienteEmail: clientEmail,
                        clienteEmpresa: clientEmpresa || "",
                        conteudo: filledContent,
                        valor: valorFinal,
                        propostaId: p.id,
                        modeloId: MODELOS_PADRAO[0].id,
                        observacoes: `Gerado automaticamente a partir da proposta: ${p.titulo}`
                      }))
                      
                      toast({
                        title: "Dados da Proposta Carregados!",
                        description: `O formulário foi preenchido com as informações de ${clientName}.`
                      })
                    }
                  }}
                >
                  <SelectTrigger id="prefillProposta" className="mt-1">
                    <SelectValue placeholder="Selecione um orçamento para importar dados..." />
                  </SelectTrigger>
                  <SelectContent>
                    {propostas.length === 0 ? (
                      <SelectItem value="none" disabled>Nenhum orçamento encontrado</SelectItem>
                    ) : (
                      propostas.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.titulo} — {p.cliente_nome} ({p.status === "aceita" ? "Aceita" : p.status})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="cTitulo">Título *</Label>
              <Input id="cTitulo" value={form.titulo} onChange={e => setField("titulo", e.target.value)} placeholder="Ex: Contrato de Gestão — Edifício Central" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome do Cliente *</Label>
                <Input value={form.clienteNome} onChange={e => setField("clienteNome", e.target.value)} placeholder="Nome" className="mt-1" />
              </div>
              <div>
                <Label>Empresa / Condomínio</Label>
                <Input value={form.clienteEmpresa ?? ""} onChange={e => setField("clienteEmpresa", e.target.value)} placeholder="Empresa" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>E-mail</Label>
                <Input type="email" value={form.clienteEmail} onChange={e => setField("clienteEmail", e.target.value)} placeholder="email@exemplo.com" className="mt-1" />
              </div>
              <div>
                <Label>Valor (R$)</Label>
                <Input type="number" min="0" step="0.01" value={form.valor} onChange={e => setField("valor", Number(e.target.value))} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data de Início</Label>
                <Input type="date" value={form.dataInicio} onChange={e => setField("dataInicio", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Data de Término</Label>
                <Input type="date" value={form.dataFim ?? ""} onChange={e => setField("dataFim", e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="cSenha">Senha de Visualização (Opcional - LGPD)</Label>
              <Input id="cSenha" type="text" value={form.senha ?? ""} onChange={e => setField("senha", e.target.value)} placeholder="Defina uma senha de segurança" className="mt-1" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Conteúdo do Contrato</Label>
                <div className="flex gap-2">
                  {profile?.role === "superadmin" && (
                    <Button size="sm" variant="outline" className="text-xs gap-1 h-7 bg-blue-50 text-blue-700 border-blue-200 hover:bg-cyan-50" onClick={() => setIsAiDialogOpen(true)}>
                      <Sparkles className="h-3 w-3" />
                      Gerar com IA
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => setModelosOpen(true)}>
                    <FileText className="h-3 w-3" />
                    Usar Modelo
                  </Button>
                </div>
              </div>
              <Textarea value={form.conteudo} onChange={e => setField("conteudo", e.target.value)} placeholder="Cole ou escreva o conteúdo do contrato. Use {{NOME_CLIENTE}}, {{VALOR_TOTAL}}, etc. como placeholders." className="min-h-[180px] font-mono text-xs" />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={form.observacoes ?? ""} onChange={e => setField("observacoes", e.target.value)} placeholder="Observações internas..." className="mt-1 min-h-[60px]" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">Salvar Contrato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modelos */}
      <Dialog open={modelosOpen} onOpenChange={setModelosOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modelos de Contrato</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {modelos.map(m => (
              <div key={m.id} className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer" onClick={() => useModelo(m)}>
                <p className="font-medium text-gray-900">{m.nome}</p>
                {m.descricao && <p className="text-sm text-gray-500">{m.descricao}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Viewer */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.titulo}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400">Cliente</p>
                  <p className="font-medium">{viewing.clienteNome}</p>
                  {viewing.clienteEmpresa && <p className="text-gray-500">{viewing.clienteEmpresa}</p>}
                </div>
                <div>
                  <p className="text-xs text-gray-400">Status / Valor</p>
                  <Tag tone={STATUS_TONE[viewing.status]} dot className="mb-1">{STATUS_LABELS[viewing.status]}</Tag>
                  <p className="font-semibold tabular-nums text-foreground">{fmtCurrency(viewing.valor)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Link de Assinatura (Público)</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full min-w-0">
                  <div className="min-w-0 flex-1 w-full overflow-hidden bg-gray-100 px-2.5 py-1.5 rounded border border-gray-200">
                    <code className="text-[11px] break-all block text-gray-600 select-all font-mono leading-relaxed">{`${window.location.origin}/c/${viewing.id}`}</code>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto flex-shrink-0">
                    <Button size="sm" variant="outline" className="gap-1 flex-1 sm:flex-initial h-9" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/c/${viewing.id}`); toast({ title: "Copiado!" }) }}>
                      <Copy className="h-3.5 w-3.5" />
                      Copiar
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1 h-9 px-3" asChild>
                      <a href={`${window.location.origin}/c/${viewing.id}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              {viewing.conteudo && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">Conteúdo</p>
                  <pre className="bg-gray-50 border rounded-lg p-3 text-xs whitespace-pre-wrap font-mono">{viewing.conteudo}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo da IA */}
      <Dialog open={isAiDialogOpen} onOpenChange={setIsAiDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-cyan-500 animate-pulse" />
              Assistente de Contratos com IA
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              A chave da IA fica guardada no servidor (Edge Function <code>ai-generate</code>) e não é enviada ao navegador.
            </p>

            {(
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Modelo da IA</Label>
                    <Select value={aiModel} onValueChange={setAiModel}>
                      <SelectTrigger className="mt-1 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {aiModels.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">Contrato Alvo</Label>
                    <div className="mt-1.5 p-2 bg-gray-100 rounded text-xs text-gray-600 font-medium truncate">
                      {form.titulo || "Nenhum contrato selecionado (Preencha os campos básicos no editor)"}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-700">O que o contrato deve conter? (Escopo, regras, multas, vigência, etc.)</Label>
                  <Textarea
                    placeholder="Ex: Prestação de serviço de manutenção de elevadores, com visitas mensais, cobertura de peças básicas, multa rescisória de 10% do valor restante, vigência de 12 meses."
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    className="min-h-[100px] text-sm"
                    disabled={isGenerating}
                  />
                </div>

                <Button
                  onClick={handleGenerateContract}
                  disabled={isGenerating || !aiPrompt.trim()}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Gerando contrato...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Gerar Texto do Contrato
                    </>
                  )}
                </Button>

                {/* Exibição do resultado */}
                {generatedContent && (
                  <div className="space-y-2 border-t pt-4">
                    <Label className="text-xs font-semibold text-gray-700">Texto Gerado pela IA</Label>
                    <pre className="bg-gray-50 border rounded-lg p-3 text-[11px] max-h-[180px] overflow-y-auto font-mono whitespace-pre-wrap">
                      {generatedContent}
                    </pre>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setGeneratedContent("")} className="flex-1">
                        Descartar
                      </Button>
                      <Button onClick={applyGeneratedContent} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        Aplicar ao Editor
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  )
}

export default Contratos
